/**
 * WebAuthn Utility Functions
 * Base64URL encoding/decoding for passkey credentials
 */

/**
 * Convert base64url string to ArrayBuffer
 * @param {string} base64url - Base64URL encoded string
 * @returns {ArrayBuffer} - Decoded ArrayBuffer
 */
export function base64urlToBuffer(base64url) {
  // Add padding if needed
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/') + padding;

  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes.buffer;
}

/**
 * Convert ArrayBuffer to base64url string
 * @param {ArrayBuffer} buffer - ArrayBuffer to encode
 * @returns {string} - Base64URL encoded string
 */
export function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';

  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * Check if WebAuthn is supported in the current browser
 * @returns {boolean} - True if WebAuthn is supported
 */
export function isWebAuthnSupported() {
  return window.PublicKeyCredential !== undefined &&
         navigator.credentials !== undefined;
}

/**
 * Get a user-friendly error message for WebAuthn errors
 * @param {Error} error - WebAuthn error
 * @returns {string} - User-friendly error message
 */
export function getWebAuthnErrorMessage(error) {
  if (!error) return 'Unknown error occurred';

  switch (error.name) {
    case 'NotAllowedError':
      return 'Operation cancelled or timed out. Please try again.';
    case 'InvalidStateError':
      return 'This passkey is already registered on this device.';
    case 'NotSupportedError':
      return 'Passkeys are not supported in this browser. Please use Chrome, Safari, or Edge.';
    case 'SecurityError':
      return 'Security error. Make sure you are using HTTPS.';
    case 'AbortError':
      return 'Operation was aborted. Please try again.';
    case 'ConstraintError':
      return 'Authentication constraint failed.';
    case 'NetworkError':
      return 'Network error occurred. Please check your connection.';
    default:
      return error.message || 'An unexpected error occurred. Please try again.';
  }
}
